/**
 * ONE labour-rates source (Phase 4): cost rate and charge-out rate.
 * Cost rate feeds estimates & budgets. Charge-out rate feeds the cost report
 * when 'Use charge-out rates' is ticked.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import NumberInput from "@/components/NumberInput";
import SaveIndicator from "@/components/SaveIndicator";
import useAutosave from "@/hooks/useAutosave";

export default function LabourRatesConfig() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [projectDiffs, setProjectDiffs] = useState(null);
  const [loading, setLoading] = useState(false);
  const autosave = useAutosave(async (_k, patch) => { await api.patch("/settings", patch); });
  function updS(patch) { setS(prev => { const next = { ...prev, ...patch }; autosave.schedule("labour_rates", next); return next; }); }

  useEffect(() => { api.get("/settings").then(r => setS({
    labour_cost_rate: r.data.labour_cost_rate ?? 65,
    labour_chargeout_rate: r.data.labour_chargeout_rate ?? 95,
  })); }, []);

  async function save() {
    setMsg(""); setErr("");
    try {
      await api.patch("/settings", s);
      setMsg("Saved ✓ — Estimates and the Cost report now read these rates.");
      setTimeout(() => setMsg(""), 3500);
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
  }

  async function checkImpact() {
    setLoading(true);
    try {
      const { data: projects } = await api.get("/projects");
      const rows = [];
      for (const p of projects.slice(0, 50)) {
        if (p.deleted_at) continue;
        try {
          const { data: est } = await api.get(`/projects/${p.id}/estimate`);
          const curRate = est.totals?.labour_default_rate || 0;
          const matHours = est.totals?.material_labour_hours_total || 0;
          const oldDollars = matHours * curRate;
          const newDollars = matHours * (s.labour_cost_rate || 0);
          rows.push({
            id: p.id, name: p.name,
            current_rate: curRate, new_rate: s.labour_cost_rate,
            mat_hours: matHours, old_dollars: oldDollars, new_dollars: newDollars,
            diff: newDollars - oldDollars,
          });
        } catch {}
      }
      setProjectDiffs(rows);
    } finally { setLoading(false); }
  }

  if (!s) return null;
  return (
    <div className="card space-y-3" data-testid="labour-rates-config">
      {msg && <div className="text-green-400 text-sm">{msg}</div>}
      {err && <div className="text-red-500 text-sm">{err}</div>}
      <p className="text-sm opacity-70">
        <b className="text-silver">Cost rate</b> — what an hour of your labour actually costs. Estimates and stage budgets use this.
        <br/>
        <b className="text-silver">Charge-out rate</b> — what you bill the client per hour. The Cost report uses this when <i>Use charge-out rates</i> is ticked.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="text-sm">Cost rate ($/h)
          <NumberInput className="input" money value={s.labour_cost_rate} onChange={e=>updS({labour_cost_rate:parseFloat(e.target.value||0)})} data-testid="lr-cost"/>
        </label>
        <label className="text-sm">Charge-out rate ($/h)
          <NumberInput className="input" money value={s.labour_chargeout_rate} onChange={e=>updS({labour_chargeout_rate:parseFloat(e.target.value||0)})} data-testid="lr-chargeout"/>
        </label>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <button className="btn-silver" onClick={save} data-testid="btn-lr-save">Save</button>
        <SaveIndicator status={autosave.status} data-testid="lr-save-indicator"/>
        <button className="btn-ghost" onClick={checkImpact} disabled={loading} data-testid="btn-lr-impact">
          {loading ? "Checking…" : "Check impact on existing projects"}
        </button>
      </div>

      {projectDiffs && (
        <div data-testid="lr-impact-table">
          <h3 className="text-silver text-sm mt-3 mb-1">Before / after per project</h3>
          <p className="text-xs opacity-70 mb-2">
            Totals already stored on each estimate are NOT touched until you open that estimate and re-save. 2638 Barracks is highlighted — it will not shift silently.
          </p>
          <table className="table text-xs">
            <thead><tr><th>Project</th><th className="text-right">Mat hours</th><th className="text-right">Current rate</th><th className="text-right">New rate</th><th className="text-right">Current $</th><th className="text-right">New $</th><th className="text-right">Diff</th><th></th></tr></thead>
            <tbody>{projectDiffs.map(r => {
              const barracks = (r.name || "").toLowerCase().includes("barracks");
              return (
                <tr key={r.id} data-testid={`lr-diff-${r.id}`} className={barracks ? "bg-amber-500/10" : ""}>
                  <td>{r.name}{barracks && <span className="text-amber-300 ml-1 text-[10px]">(⚠ hold)</span>}</td>
                  <td className="text-right">{r.mat_hours.toFixed(2)}</td>
                  <td className="text-right">${r.current_rate.toFixed(0)}</td>
                  <td className="text-right">${r.new_rate.toFixed(0)}</td>
                  <td className="text-right">${r.old_dollars.toFixed(2)}</td>
                  <td className="text-right">${r.new_dollars.toFixed(2)}</td>
                  <td className={`text-right ${r.diff === 0 ? "" : "text-amber-300"}`}>
                    {r.diff >= 0 ? "+" : ""}${r.diff.toFixed(2)}
                  </td>
                  <td>
                    {r.diff === 0 ? <span className="opacity-50 text-xs">matches</span> : (
                      <button className="btn-silver text-xs" data-testid={`btn-apply-${r.id}`}
                              onClick={async () => {
                                if (!window.confirm(`Apply new rate $${r.new_rate}/h to "${r.name}"? This changes its stored estimate totals.`)) return;
                                await api.post("/labour-rates/apply-to-project", { project_id: r.id });
                                checkImpact();
                              }}>Apply</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {projectDiffs.length === 0 && <tr><td colSpan="8" className="opacity-50">No projects to compare.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
